import { action, computed, makeObservable, override, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { redirectToRoute } from 'routes/redirectToRoute';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';
import IHealthQuestionnairePage from 'store/modules/subscriber/health-questionnaire/iHealthQuestionnairePage';
import { completeSubscriberChecklistStep } from 'api/operations/electionService';
import { SubChecklistCard } from 'types/electionService';
import { ImqBrokerHealthQuestionnaireDomainParams } from '../health-questionnaire-entry/pageStore';

export default class ImqBrokerHealthQuestionnairePageStore extends IHealthQuestionnairePage {
  protected readonly _domainParams: ImqBrokerHealthQuestionnaireDomainParams;
  public readonly flowDelegate: QuoteFlowDelegate;

  public override get domainParams(): ImqBrokerHealthQuestionnaireDomainParams {
    return this._domainParams;
  }

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || this.isPageContentLoading;
  }

  public editCategory(categoryName: string): void {
    const { groupId, periodId, cartId, subscriberId, entityId } = this.domainParams;

    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY, {
      groupId,
      periodId,
      cartId,
      subscriberId,
      entityId,
      category: encodeURIComponent(categoryName),
    });
  }

  protected onCompleteRedirect(): void {
    const { groupId, periodId, cartId } = this.domainParams;
    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES, { groupId, periodId, cartId });
  }

  public override async completeChecklistStep(): Promise<void> {
    await completeSubscriberChecklistStep({
      subId: this._domainParams.subscriberId,
      entityId: this._domainParams.subscriberId,
      card: SubChecklistCard.HEALTH_QUESTIONNAIRE,
    });
    runInAction(() => {
      this.onCompleteRedirect();
    });
  }

  public async getFlowAndPageInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);
    await this.flowDelegate.getFlowInitialData();
    await this.getInitialData();
    this.flowDelegate.setIsInitialDataLoading(false);
  }

  constructor(domainParams: ImqBrokerHealthQuestionnaireDomainParams) {
    super(domainParams);
    this._domainParams = domainParams;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainParams.groupId,
        periodId: domainParams.periodId,
        cartId: domainParams.cartId,
      },
      nextPageId: QuoteRouteV2Id.IMQ_ADD_EMPLOYEES,
    });

    makeObservable<ImqBrokerHealthQuestionnairePageStore, 'onCompleteRedirect'>(this, {
      isInitialLoading: computed,
      domainParams: override,
      editCategory: action.bound,
      onCompleteRedirect: action.bound,
      getFlowAndPageInitialData: action.bound,
    });
  }
}

const ImqBrokerHealthQuestionnairePageStoreContext = createContext(
  new ImqBrokerHealthQuestionnairePageStore({} as ImqBrokerHealthQuestionnaireDomainParams),
);

export const useImqBrokerHealthQuestionnairePageStore = (): ImqBrokerHealthQuestionnairePageStore => {
  return useContext(ImqBrokerHealthQuestionnairePageStoreContext);
};

export { ImqBrokerHealthQuestionnairePageStoreContext };
